import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useSelector } from 'react-redux';
import { getDataAPI } from '../../utils/fetchData';
import { getUploadedFiles } from '../../utils/uploadedFilesTracker';

const DataView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cachedBookings = useSelector((state) => state.dataCache?.bookings?.data);
  const [bookings, setBookings] = useState([]);
  const [labelData, setLabelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(
    location.state?.category || 'all'
  ); // 'all', 'bookings', 'label'

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Load uploaded files (not processed data) using utility function
      try {
        // Get files from utility (which uses localStorage)
        const allFiles = getUploadedFiles();
        const bookingFiles = allFiles.filter(f => f.dataType === 'booking' || f.type === 'booking');
        const labelFiles = allFiles.filter(f => f.dataType === 'label' || f.type === 'label');
        
        setBookings(bookingFiles);
        setLabelData(labelFiles);
        
        // Also try API to sync if available
        try {
          const filesResponse = await getDataAPI('user/get-uploaded-files', token);
          if (filesResponse.data.status === 200 && filesResponse.data.data) {
            const files = Array.isArray(filesResponse.data.data) 
              ? filesResponse.data.data 
              : filesResponse.data.data.files || [];
            
            const apiBookingFiles = files.filter(f => f.dataType === 'booking' || f.type === 'booking');
            const apiLabelFiles = files.filter(f => f.dataType === 'label' || f.type === 'label');
            
            // Use API data if available, otherwise use localStorage
            if (apiBookingFiles.length > 0 || apiLabelFiles.length > 0) {
              setBookings(apiBookingFiles);
              setLabelData(apiLabelFiles);
            }
          }
        } catch (apiError) {
          // API endpoint might not exist yet, use localStorage data
          console.warn('Upload files API not available, using localStorage:', apiError);
        }
      } catch (error) {
        console.error('Error loading uploaded files:', error);
        setBookings([]);
        setLabelData([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDisplayData = () => {
    switch (activeCategory) {
      case 'bookings':
        return bookings.map(item => ({ ...item, type: 'booking' }));
      case 'label':
        return labelData.map(item => ({ ...item, type: 'label' }));
      default:
        return [
          ...bookings.map(item => ({ ...item, type: 'booking' })),
          ...labelData.map(item => ({ ...item, type: 'label' }))
        ];
    }
  };

  const displayData = getDisplayData();

  const handleItemClick = (item) => {
    // For uploaded files, we might want to show file details or download
    // For now, just show the file info
    console.log('File clicked:', item);
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Uploaded Data</span>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  const uploadType = activeCategory === 'bookings' ? 'booking' : activeCategory === 'label' ? 'label' : 'booking';
                  navigate('/connectors/data-upload', { state: { type: uploadType } });
                }}
              >
                Upload
              </span>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="w-full mb-6">
            <div className="flex px-4">
              <button
                onClick={() => setActiveCategory('all')}
                className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                  activeCategory === 'all' 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                }`}
              >
                All
                {activeCategory === 'all' && (
                  <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FF2300]"></div>
                )}
              </button>
              <button
                onClick={() => setActiveCategory('bookings')}
                className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                  activeCategory === 'bookings' 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                }`}
              >
                Bookings ({bookings.length})
                {activeCategory === 'bookings' && (
                  <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FF2300]"></div>
                )}
              </button>
              <button
                onClick={() => setActiveCategory('label')}
                className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                  activeCategory === 'label' 
                    ? 'text-white' 
                    : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                }`}
              >
                Label ({labelData.length})
                {activeCategory === 'label' && (
                  <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FF2300]"></div>
                )}
              </button>
            </div>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-white text-[12px] opacity-70">Loading...</div>
              </div>
            ) : displayData.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No {activeCategory === 'all' ? 'data' : activeCategory === 'bookings' ? 'bookings' : 'label'} uploaded yet
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  {activeCategory === 'all' 
                    ? 'Upload files and documents so Dizee can map them to the right apps.'
                    : activeCategory === 'bookings'
                    ? 'Upload show advances, deal sheets, and settlement PDFs to get started.'
                    : 'Upload label, distributor, and DSP royalty reports and statements.'
                  }
                </p>
                <button
                  onClick={() => {
                    const uploadType = activeCategory === 'bookings' ? 'booking' : activeCategory === 'label' ? 'label' : 'booking';
                    navigate('/connectors/data-upload', { state: { type: uploadType } });
                  }}
                  className="bg-[#B08A4C] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#9A7A42] transition-colors"
                >
                  Upload data
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {displayData.map((item, index) => (
                  <div
                    key={item._id || item.id || item.fileId || index}
                    onClick={() => handleItemClick(item)}
                    className="bg-black border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.3)] cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-10 h-10 bg-[rgba(4,205,208,0.1)] rounded-lg flex items-center justify-center">
                            {item.fileName?.endsWith('.pdf') ? (
                              <svg className="w-5 h-5 text-[#FF2300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                            ) : item.fileName?.endsWith('.csv') || item.fileName?.endsWith('.xlsx') || item.fileName?.endsWith('.xls') ? (
                              <svg className="w-5 h-5 text-[#FF2300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5 text-[#FF2300]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <span className="text-white text-[12px] font-semibold block">
                              {item.fileName || item.name || 'Untitled File'}
                            </span>
                            <span className="text-[#FF2300] text-[10px] bg-[rgba(255,35,0,0.1)] px-2 py-0.5 rounded inline-block mt-1">
                              {item.type === 'booking' || item.dataType === 'booking' ? 'Booking' : 'Label'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-white text-[10px] opacity-50 mt-2">
                          {item.uploadDate && (
                            <span>Uploaded {formatDate(item.uploadDate)}</span>
                          )}
                          {item.fileSize && (
                            <span>• {formatFileSize(item.fileSize)}</span>
                          )}
                          {item.fileType && (
                            <span>• {item.fileType.toUpperCase()}</span>
                          )}
                        </div>
                        {item.status && (
                          <div className="mt-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded ${
                              item.status === 'processed' 
                                ? 'bg-green-500 bg-opacity-20 text-green-500'
                                : item.status === 'processing'
                                ? 'bg-yellow-500 bg-opacity-20 text-yellow-500'
                                : 'bg-[rgba(255,255,255,0.1)] text-white opacity-70'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default DataView;

